import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoadingShim } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";
import { useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { useFeature } from "byzantine";
import { useFingerPrintData } from "./fingerprint";
import { checkAuthSession } from "../src/utils";

const EmailChangeConfirmScreen = () => {
  const [searchParams] = useSearchParams();
  const { l10n } = useLocalization();
  const { featureEquals } = useFeature();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const { deviceId, deviceRequestId, error, isEnabled } =
    useFingerPrintData("email_change");
  const lastSubmittedKey = useRef<string | null>(null);
  const hasMonitoring = featureEquals(
    "third_party_ach_transaction_evaluations",
    ["report", "decision"]
  );

  useEffect(() => {
    const changeKey = searchParams.get("key");
    if (!changeKey) {
      sendNotification({
        type: "negative",
        text: l10n.getString("email-change-confirm-key-missing"),
      });
      navigate("/profile");
      return;
    }

    const confirmEmailChange = async () => {
      try {
        await ApiHttp.fetch(
          "users/email_change_confirm",
          { method: "POST" },
          {
            key: changeKey,
            device_id: deviceId,
            device_request_id: deviceRequestId,
          }
        );
        sendNotification({
          type: "success",
          text: l10n.getString("email-change-confirm-success"),
        });
        navigate("/profile");
      } catch (err) {
        sendNotification({
          type: "negative",
          text: l10n.getString("email-change-confirm-failed"),
        });
        navigate("/profile");
        await checkAuthSession(
          l10n.getString("logout-message-blocked-and-locked")
        );
      }
    };

    if (
      lastSubmittedKey.current !== changeKey &&
      (!hasMonitoring || !isEnabled || error || (deviceId && deviceRequestId))
    ) {
      lastSubmittedKey.current = changeKey;
      confirmEmailChange().catch(() => {
        sendNotification({
          type: "negative",
          text: l10n.getString("email-change-confirm-failed"),
        });
        navigate("/profile");
      });
    }
  }, [
    deviceId,
    deviceRequestId,
    error,
    hasMonitoring,
    isEnabled,
    searchParams,
    sendNotification,
    l10n,
    navigate,
  ]);

  return (
    <LoadingShim isLoading={true}>
      <div />
    </LoadingShim>
  );
};

export default EmailChangeConfirmScreen;
